import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { LogOut, RefreshCcw, Download } from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';
import { OrderWithItems } from '../types';
import OrderList from '../components/admin/OrderList';
import OrderDetails from '../components/admin/OrderDetails';
import DeleteConfirmation from '../components/admin/DeleteConfirmation';

export default function AdminPage() {
  const [orders, setOrders] = useState<OrderWithItems[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<OrderWithItems | null>(null);
  const [deleteConfirmOrder, setDeleteConfirmOrder] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) {
        navigate('/admin/login');
        return;
      }
      fetchOrders();
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session) {
        navigate('/admin/login');
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: ordersData, error: ordersError } = await supabase
        .from('orders')
        .select('*')
        .order('created_at', { ascending: false });

      if (ordersError) throw ordersError;

      const ordersWithItems = await Promise.all(
        ordersData.map(async (order) => {
          const { data: itemsData, error: itemsError } = await supabase
            .from('order_items')
            .select('*')
            .eq('order_id', order.id);

          if (itemsError) throw itemsError;

          return {
            ...order,
            items: itemsData
          };
        })
      );

      setOrders(ordersWithItems);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Wystąpił błąd podczas pobierania zamówień');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (orderId: string, newStatus: OrderWithItems['status']) => {
    try {
      const { error } = await supabase
        .from('orders')
        .update({ status: newStatus })
        .eq('id', orderId);

      if (error) throw error;

      toast.success('Status zamówienia został zaktualizowany');
      fetchOrders();
    } catch (err) {
      toast.error('Błąd podczas aktualizacji statusu');
      console.error('Error updating status:', err);
    }
  };

  const handleDeleteOrder = async (orderId: string) => {
    try {
      const { error: itemsError } = await supabase
        .from('order_items')
        .delete()
        .eq('order_id', orderId);

      if (itemsError) throw itemsError;

      const { error: orderError } = await supabase
        .from('orders')
        .delete()
        .eq('id', orderId);

      if (orderError) throw orderError;

      toast.success('Zamówienie zostało usunięte');
      setDeleteConfirmOrder(null);
      fetchOrders();
    } catch (err) {
      toast.error('Błąd podczas usuwania zamówienia');
      console.error('Error deleting order:', err);
    }
  };

  const handleExportCSV = () => {
    if (orders.length === 0) {
      toast.error('Brak zamówień do eksportu');
      return;
    }

    const headers = [
      'Data', 'Imię', 'Nazwisko', 'Email', 'Telefon', 'Adres', 'NIP',
      'Data dostawy', 'Transport', 'Sadzenie', 'Odbiór osobisty', 'Dystans (km)',
      'Koszt transportu', 'Koszt sadzenia', 'Hurtowe', 'Status', 'Suma',
      'Produkty', 'Dodatkowe info'
    ];

    const escapeCSV = (val: string) => {
      if (val.includes(',') || val.includes('"') || val.includes('\n')) {
        return `"${val.replace(/"/g, '""')}"`;
      }
      return val;
    };

    const rows = orders.map((order) => {
      const products = order.items
        .map((item) => `${item.plant_name} ${item.height} x${item.quantity} (${item.price.toFixed(2)} zl)`)
        .join('; ');

      return [
        order.created_at ? new Date(order.created_at).toLocaleString('pl-PL') : '',
        order.first_name,
        order.last_name,
        order.email,
        order.phone,
        order.address || '',
        order.vat_number || '',
        order.delivery_date ? new Date(order.delivery_date).toLocaleDateString('pl-PL') : '',
        order.requires_transport ? 'Tak' : 'Nie',
        order.requires_planting ? 'Tak' : 'Nie',
        order.pickup_in_person ? 'Tak' : 'Nie',
        order.distance_km?.toString() || '0',
        order.transport_cost?.toFixed(2) || '0.00',
        order.planting_cost?.toFixed(2) || '0.00',
        order.is_wholesale ? 'Tak' : 'Nie',
        order.status,
        order.total_amount.toFixed(2),
        products,
        order.additional_info || '',
      ].map(escapeCSV);
    });

    const csvContent = '\uFEFF' + [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `zamowienia-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Wyeksportowano do CSV');
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/admin/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 p-4 sm:p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-400"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 p-4 sm:p-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-900/50 border border-red-700 rounded-lg p-4 text-red-200">
            {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100">
      <Toaster position="top-right" />
      <div className="bg-gray-800 shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <h1 className="text-xl sm:text-2xl font-bold">Panel Administratora</h1>
            <div className="flex items-center space-x-2 sm:space-x-4">
              <button
                onClick={handleExportCSV}
                className="inline-flex items-center px-3 py-2 border border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-200 bg-gray-700 hover:bg-gray-600"
              >
                <Download className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Eksport CSV</span>
              </button>
              <button
                onClick={fetchOrders}
                className="inline-flex items-center px-3 py-2 border border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-200 bg-gray-700 hover:bg-gray-600"
              >
                <RefreshCcw className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Odśwież</span>
              </button>
              <button
                onClick={handleLogout}
                className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
              >
                <LogOut className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Wyloguj</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <OrderList
          orders={orders}
          onViewOrder={setSelectedOrder}
          onDeleteOrder={setDeleteConfirmOrder}
          onStatusChange={handleStatusChange}
        />
      </div>

      {selectedOrder && (
        <OrderDetails
          order={selectedOrder}
          onClose={() => setSelectedOrder(null)}
        />
      )}

      {deleteConfirmOrder && (
        <DeleteConfirmation
          onConfirm={() => handleDeleteOrder(deleteConfirmOrder)}
          onCancel={() => setDeleteConfirmOrder(null)}
        />
      )}
    </div>
  );
}